import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isEUCountry } from "@/i18n/countries";
import { toast } from "sonner";
import { Building2, Users, MessagesSquare, Landmark, ArrowRight, Bell, MapPin, Plus, Building, Map, Flag, Stars } from "lucide-react";

const LEVEL_META = {
  city: { icon: Building, bg: "bg-primary text-white" },
  region: { icon: Map, bg: "bg-secondary text-white" },
  country: { icon: Flag, bg: "bg-foreground text-white" },
  eu: { icon: Stars, bg: "bg-primary text-white" },
};

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [spaces, setSpaces] = useState([]);
  const [unread, setUnread] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [sForm, setSForm] = useState({ name: "", description: "", topics: "" });
  const [creating, setCreating] = useState(false);

  const loadSpaces = () => api.get("/spaces").then((r) => setSpaces(r.data)).catch(() => setSpaces([]));
  useEffect(() => {
    loadSpaces();
    api.get("/notifications").then((r) => setUnread(r.data.filter((n) => !n.read).length)).catch(() => {});
  }, [user?.id, user?.space_ids?.length]);

  const createSpace = async (e) => {
    e.preventDefault(); setCreating(true);
    try {
      const { data } = await api.post("/spaces", { name: sForm.name, description: sForm.description, topics: sForm.topics.split(",").map((x) => x.trim()).filter(Boolean) });
      await refresh();
      toast.success(t("Space created"));
      nav(`/space/${data.slug}`);
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  const loc = user.location || {};
  const jurisdictions = [
    loc.city && { level: "city", label: t("City"), value: loc.city },
    loc.region && { level: "region", label: t("State or Region"), value: loc.region },
    loc.country && { level: "country", label: t("Country"), value: loc.country },
    isEUCountry(loc.country) && { level: "eu", label: t("European Union"), value: "European Union" },
  ].filter(Boolean);

  return (
    <div className="space-y-12 max-w-7xl mx-auto" data-testid="dashboard">
      <div className="nb-card bg-civic-pattern p-8 sm:p-10 rounded-3xl">
        <span className="overline">{t("Deliber.Act platform")}</span>
        <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tight mt-1">{t("Welcome, {{name}}", { name: user.name })}</h1>
        <p className="text-foreground/70 mt-3 max-w-2xl font-medium">{t("Deliberate within your jurisdiction or join a dedicated Deliberative Space to build focused programmes and proposals.")}</p>
      </div>

      {unread > 0 && (
        <Link to="/notifications" className="nb-card nb-hover flex items-center gap-3 bg-secondary text-white p-4" data-testid="dash-notif-banner">
          <span className="w-9 h-9 rounded-lg border-2 border-white flex items-center justify-center shrink-0"><Bell size={18} /></span>
          <span className="text-sm font-bold">{t("You have {{count}} new notifications — invitations, votes and updates.", { count: unread })}</span>
        </Link>
      )}

      <section>
        <div className="flex items-center gap-2 mb-4"><MapPin size={20} className="text-primary" /><h2 className="font-display text-2xl font-black tracking-tight">{t("Your jurisdictions")}</h2></div>
        <p className="text-sm text-muted-foreground mb-6 font-medium">{t("Deliberate on issues at every level of government you belong to. You're invited automatically when a debate matches your interests or expertise.")}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {jurisdictions.map((j) => {
            const meta = LEVEL_META[j.level] || LEVEL_META.city;
            const Icon = meta.icon;
            return (
              <Link key={j.level} to={`/jurisdiction/${j.level}/${encodeURIComponent(j.value)}`} data-testid={`dash-jurisdiction-${j.level}`} className="nb-card nb-hover p-6 block">
                <span className={`w-12 h-12 rounded-xl border-2 border-foreground flex items-center justify-center mb-4 ${meta.bg}`}><Icon size={22} /></span>
                <span className="nb-pill bg-white text-foreground text-[10px] uppercase tracking-wider px-2.5 py-0.5">{j.label}</span>
                <h3 className="font-display font-extrabold tracking-tight mt-3 text-lg">{j.value}</h3>
                <span className="text-xs text-primary font-bold inline-flex items-center gap-1 mt-2">{t("View debates")} <ArrowRight size={12} /></span>
              </Link>
            );
          })}
          {jurisdictions.length === 0 && <p className="text-sm text-muted-foreground">{t("Add your location in your profile to see jurisdiction debates.")}</p>}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between gap-2 mb-6">
          <div className="flex items-center gap-2"><Building2 size={20} className="text-secondary" /><h2 className="font-display text-2xl font-black tracking-tight">{t("Deliberative Spaces")}</h2></div>
          <button onClick={() => setShowCreate(!showCreate)} data-testid="dash-create-space-btn" className="nb-btn bg-primary text-white text-sm px-4 py-2"><Plus size={16} /> {t("Create a space")}</button>
        </div>
        {showCreate && (
          <form onSubmit={createSpace} className="nb-card p-6 space-y-3 mb-6" data-testid="create-space-form">
            <p className="overline">{t("Create your own deliberative space")}</p>
            <input required placeholder={t("Space name")} value={sForm.name} onChange={(e) => setSForm({ ...sForm, name: e.target.value })} className={ic} data-testid="space-name-input" />
            <textarea placeholder={t("What is this space about?")} value={sForm.description} onChange={(e) => setSForm({ ...sForm, description: e.target.value })} className={ic + " h-20"} data-testid="space-desc-input" />
            <input placeholder={t("Topics (comma)")} value={sForm.topics} onChange={(e) => setSForm({ ...sForm, topics: e.target.value })} className={ic} data-testid="space-topics-input" />
            <p className="text-xs text-muted-foreground font-medium">{t("You'll become the organiser and can invite others with a shareable link.")}</p>
            <button disabled={creating} data-testid="space-create-submit" className="nb-btn bg-secondary text-white text-sm px-4 py-2 disabled:opacity-60">{creating ? t("Creating…") : t("Create space")}</button>
          </form>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {spaces.map((s) => {
            const joined = user.space_ids?.includes(s.id);
            return (
              <Link key={s.id} to={`/space/${s.slug}`} data-testid="dash-space-card" className="nb-card nb-hover p-6 block">
                <div className="flex items-center gap-2 mb-2">
                  {joined ? <span className="nb-pill bg-secondary text-white text-[10px] uppercase tracking-wider px-2.5 py-0.5">{t("member")}</span> : <span className="nb-pill bg-white text-foreground text-[10px] uppercase tracking-wider px-2.5 py-0.5">{t("open")}</span>}
                  <span className="text-xs text-muted-foreground font-medium">{t("{{count}} parties", { count: s.party_count })}</span>
                </div>
                <h3 className="font-display text-xl font-extrabold tracking-tight">{s.name}</h3>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2 font-medium">{s.description}</p>
                <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground font-semibold">
                  <span className="flex items-center gap-1"><Users size={13} /> {t("{{count}} members", { count: s.member_count })}</span>
                  <span className="flex items-center gap-1"><MessagesSquare size={13} /> {t("{{count}} debates", { count: s.debate_count })}</span>
                  <span className="ml-auto inline-flex items-center gap-1 text-primary font-bold">{t("Open")} <ArrowRight size={13} /></span>
                </div>
              </Link>
            );
          })}
          {spaces.length === 0 && <p className="text-sm text-muted-foreground">{t("No deliberative spaces yet.")}</p>}
        </div>
      </section>

      <section>
        <h2 className="font-display text-2xl font-black tracking-tight mb-6">{t("Explore")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <Link to="/representatives" data-testid="dash-reps" className="nb-card nb-hover p-6 block">
            <span className="w-12 h-12 rounded-xl border-2 border-foreground bg-foreground text-white flex items-center justify-center mb-3"><Landmark size={22} /></span>
            <h3 className="font-display font-extrabold tracking-tight text-lg">{t("Representatives & parties")}</h3>
            <p className="text-xs text-muted-foreground mt-1 font-medium">{t("Track who is acting on approved community proposals.")}</p>
          </Link>
          <Link to="/profile" data-testid="dash-profile" className="nb-card nb-hover p-6 block">
            <span className="w-12 h-12 rounded-xl border-2 border-foreground bg-secondary text-white flex items-center justify-center mb-3"><Users size={22} /></span>
            <h3 className="font-display font-extrabold tracking-tight text-lg">{t("Your profile")}</h3>
            <p className="text-xs text-muted-foreground mt-1 font-medium">{t("Manage interests, expertise and verification.")}</p>
          </Link>
        </div>
      </section>
    </div>
  );
}
const ic = "w-full nb-input px-3 py-2 text-sm";
